/**
 * Tableau Client
 * ──────────────
 * Thin wrapper over `tableau.extensions` that initialises the API, exposes
 * environment/identity info, and provides a dev-friendly mock when the bundle is
 * run outside Tableau (e.g. `npm run dev` in a plain browser). The mock lets the
 * full UI be developed and demoed without a live dashboard.
 */

import type { Combination, ExtensionConfig, Favorite, UsageEvent } from "@/types";

/** Settings key under which the admin configuration is locked into the workbook. */
const CONFIG_SETTINGS_KEY = "spc:extension-config";

/** Settings key under which usage history is locked into the workbook. */
const DATA_SETTINGS_KEY = "spc:extension-data";

export interface TableauContext {
  available: boolean;
  mode: "authoring" | "viewing" | "dev";
  tableauVersion: string;
  workbookName: string;
  dashboardName: string;
  userName: string;
  worksheets: string[];
}

/** Identify the current Tableau user.
 *
 * The Extensions API does not expose username directly for privacy reasons; the
 * canonical pattern is to read it from a dedicated workbook parameter named
 * `Tableau.User` populated with the USERNAME() calc, or fall back to the
 * authoring/viewing mode. We never request credentials or PAT tokens. */
async function resolveUserName(): Promise<string> {
  const dashboard = globalThis.tableau?.extensions?.dashboardContent?.dashboard;
  try {
    const userParam =
      (await dashboard?.findParameterByNameAsync("Tableau.User")) ??
      (await dashboard?.findParameterByNameAsync("Current User"));
    if (userParam?.currentValue?.value) return String(userParam.currentValue.value);
  } catch {
    // optional parameter — ignore
  }
  return "Tableau User";
}

export async function initializeTableau(
  onConfigure?: () => void,
): Promise<TableauContext> {
  const t = globalThis.tableau;

  // ── Dev / standalone fallback ───────────────────────────────────────────────
  if (!t?.extensions) {
    console.info("[tableau] Extensions API not present — running in dev mock mode.");
    return {
      available: false,
      mode: "dev",
      tableauVersion: "dev",
      workbookName: "Dev Workbook",
      dashboardName: "Dev Dashboard",
      userName: "Local Developer",
      worksheets: [],
    };
  }

  await t.extensions.initializeAsync(
    onConfigure ? { configure: onConfigure } : undefined,
  );

  const env = t.extensions.environment;
  const dashboard = t.extensions.dashboardContent?.dashboard;
  const userName = await resolveUserName();

  return {
    available: true,
    mode: env.mode,
    tableauVersion: env.tableauVersion,
    // Workbook name isn't exposed in every API version; read defensively.
    workbookName: t.extensions.workbook?.name ?? dashboard?.name ?? "Workbook",
    dashboardName: dashboard?.name ?? "Dashboard",
    userName,
    worksheets: dashboard?.worksheets.map((w) => w.name) ?? [],
  };
}

/** Subscribe to external parameter changes so the draft can stay in sync. */
export function onParameterChanged(
  handler: (parameterId: string) => void,
): () => void {
  const t = globalThis.tableau;
  const dashboard = t?.extensions?.dashboardContent?.dashboard;
  if (!t || !dashboard) return () => {};

  const unsubscribers: Array<() => void> = [];
  void dashboard.getParametersAsync().then((params) => {
    for (const p of params) {
      const off = p.addEventListener(t.TableauEventType.ParameterChanged, (e) => {
        void e.getParameterAsync().then((cp) => handler(cp.id));
      });
      unsubscribers.push(off);
    }
  });

  return () => unsubscribers.forEach((u) => u());
}

/**
 * Read the admin configuration locked into the workbook via the Settings API.
 * Returns null when not running in Tableau or when no config has been saved yet.
 * This is what makes the parameter include/exclude selection persist across
 * reloads and survive publishing the workbook to Server/Cloud.
 */
export function readTableauConfig(): Partial<ExtensionConfig> | null {
  const settings = globalThis.tableau?.extensions?.settings;
  if (!settings) return null;
  try {
    const raw = settings.get(CONFIG_SETTINGS_KEY);
    return raw ? (JSON.parse(raw) as Partial<ExtensionConfig>) : null;
  } catch {
    return null;
  }
}

/**
 * Lock the admin configuration into the workbook. Writing to settings + saving
 * persists the selection so it does not reset on reload. No-op outside Tableau
 * (the browser storage provider handles persistence in dev/standalone mode).
 */
export async function writeTableauConfig(config: ExtensionConfig): Promise<void> {
  const settings = globalThis.tableau?.extensions?.settings;
  if (!settings) return;
  try {
    settings.set(CONFIG_SETTINGS_KEY, JSON.stringify(config));
    await settings.saveAsync();
  } catch (err) {
    console.warn("[tableau] failed to persist config to workbook settings", err);
  }
}

// ── usage-history persistence (workbook settings) ─────────────────────────────
//
// Browser storage (localStorage / IndexedDB) does not reliably survive Tableau
// Desktop restarts, so Top Combinations history vanished on every open. The
// history is therefore also locked into the workbook via the Settings API: it
// travels with the .twb/.twbx, survives publishing, and seeds every session.
// Settings can only be SAVED in authoring mode, so viewer sessions on
// Server/Cloud read the workbook copy and keep their own additions in browser
// storage.

export interface WorkbookData {
  combinations: Combination[];
  events: UsageEvent[];
  favorites: Favorite[];
}

/** Bounds keeping the settings payload well inside Tableau's size limits. */
const MAX_WORKBOOK_COMBINATIONS = 300;
const MAX_WORKBOOK_EVENTS = 2000;

/** Read the usage history locked into the workbook (readable in every mode). */
export function readWorkbookData(): WorkbookData | null {
  const settings = globalThis.tableau?.extensions?.settings;
  if (!settings) return null;
  try {
    const raw = settings.get(DATA_SETTINGS_KEY);
    return raw ? (JSON.parse(raw) as WorkbookData) : null;
  } catch {
    return null;
  }
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Persist the usage history into the workbook. Debounced (800ms) so bursts of
 * executions coalesce into one save. Only authoring mode may call saveAsync —
 * in viewing mode this is a silent no-op and browser storage carries the data.
 */
export function writeWorkbookData(data: WorkbookData): void {
  const t = globalThis.tableau;
  const settings = t?.extensions?.settings;
  if (!settings) return;
  if (t.extensions.environment?.mode !== "authoring") return;

  const bounded: WorkbookData = {
    combinations: [...data.combinations]
      .sort((a, b) => b.usageCount - a.usageCount)
      .slice(0, MAX_WORKBOOK_COMBINATIONS),
    events: data.events.slice(-MAX_WORKBOOK_EVENTS),
    favorites: data.favorites,
  };

  try {
    settings.set(DATA_SETTINGS_KEY, JSON.stringify(bounded));
  } catch (err) {
    console.warn("[tableau] failed to stage history into workbook settings", err);
    return;
  }

  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    settings
      .saveAsync()
      .catch((err: unknown) =>
        console.warn("[tableau] failed to save history to workbook settings", err),
      );
  }, 800);
}
